import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { TableCell, TableRow, TableSkeleton } from "design-system/molecules/Table"
import { useTranslation } from "react-i18next"
import { BackofficeUnansweredQuestionTopicsResponseDataSchema } from "schema"
import { z } from "zod"

type Props = {
  company: z.infer<typeof BackofficeUnansweredQuestionTopicsResponseDataSchema>[number] | undefined
  columnCount: number
  isPending: boolean
  isError: boolean
  onRetry: () => void
}

export function BackofficeCompanyUnansweredTopics({
  company,
  columnCount,
  isPending,
  isError,
  onRetry,
}: Props) {
  const { t } = useTranslation()

  if (isError)
    return (
      <TableRow className="bg-muted/30">
        <TableCell colSpan={columnCount}>
          <div role="alert" className="flex items-center gap-2">
            {t("backoffice.knowledge.dailyTopics.error")}
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t("backoffice.knowledge.categories.retry")}
            </Button>
          </div>
        </TableCell>
      </TableRow>
    )

  if (isPending) return <TableSkeleton colSpans={[columnCount]} />

  if (!company?.topics.length)
    return (
      <TableRow className="bg-muted/30">
        <TableCell colSpan={columnCount} className="text-muted-foreground">
          {t("backoffice.knowledge.dailyTopics.empty")}
        </TableCell>
      </TableRow>
    )

  return (
    <>
      {company.topics.map((topic) => (
        <TableRow key={topic.id} className="bg-muted/30">
          <TableCell colSpan={columnCount} className="pl-10">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                from="/backoffice/knowledge"
                to="/backoffice/knowledge/topics/$topicId"
                params={{ topicId: topic.id }}
                search={(previous) => previous}
                className="text-left hover:underline"
              >
                {topic.question}
              </Link>
              <Badge size="sm" variant={"secondary"}>
                {topic.category ?? t("backoffice.knowledge.uncategorized")}
              </Badge>
              <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">
                {t("backoffice.knowledge.dailyTopics.sampleCount", {
                  count: topic.sampledQuestionCount,
                  size: topic.sampleSize,
                  percentage:
                    topic.sampleSize > 0
                      ? ((topic.sampledQuestionCount / topic.sampleSize) * 100).toFixed(0)
                      : "0",
                })}
              </span>
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}
import { Link } from "@tanstack/react-router"
